import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Menu, X, Heart } from "lucide-react";
import NotificationBell from "./NotificationBell";
import Logo from "./Logo";

export default function Navbar() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const linkCls = ({ isActive }) =>
    `font-manrope text-sm px-3 py-2 rounded-full transition-colors ${
      isActive ? "text-brand bg-brand-soft" : "text-ink-soft hover:text-brand"
    }`;

  return (
    <header
      className="sticky top-0 z-50 bg-cream/80 backdrop-blur-xl border-b border-[#EBE2E8]"
      data-testid="main-navbar"
    >
      <div className="container-x flex items-center justify-between h-16">
        <Link to="/" className="inline-flex items-center" data-testid="nav-logo">
          <Logo size={36} wordmarkClass="text-lg" />
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          <NavLink to="/browse" className={linkCls} data-testid="nav-browse">
            Browse items
          </NavLink>
          <NavLink to="/blog" className={linkCls} data-testid="nav-blog">
            Blog
          </NavLink>
          {user && (
            <>
              <NavLink to="/dashboard" className={linkCls} data-testid="nav-dashboard">
                My registry
              </NavLink>
              <NavLink to="/thank-yous" className={linkCls} data-testid="nav-thank-yous">
                Thank-yous
              </NavLink>
              <NavLink to="/settings" className={linkCls} data-testid="nav-settings">
                Settings
              </NavLink>
              {user.role === "admin" && (
                <NavLink to="/admin" className={linkCls} data-testid="nav-admin">
                  Admin
                </NavLink>
              )}
            </>
          )}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          {user ? (
            <>
              <NotificationBell />
              <span className="font-manrope text-sm text-ink-soft" data-testid="nav-display-name">
                Hi, {user.display_name || "friend"}
              </span>
              <button onClick={handleLogout} className="btn-ghost text-sm" data-testid="nav-logout-btn">
                Log out
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="btn-ghost text-sm" data-testid="nav-login-btn">
                Log in
              </Link>
              <Link to="/register" className="btn-primary text-sm" data-testid="nav-register-btn">
                <Heart className="inline h-4 w-4 mr-2" strokeWidth={1.8} /> Start yours
              </Link>
            </>
          )}
        </div>

        <button
          className="md:hidden p-2 rounded-full hover:bg-brand-soft"
          onClick={() => setOpen(!open)}
          data-testid="nav-mobile-toggle"
        >
          {open ? <X className="h-5 w-5 text-ink" /> : <Menu className="h-5 w-5 text-ink" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-[#EBE2E8] bg-white" data-testid="nav-mobile-menu">
          <div className="container-x py-4 flex flex-col gap-2">
            <NavLink to="/browse" className={linkCls} onClick={() => setOpen(false)} data-testid="nav-mobile-browse">
              Browse items
            </NavLink>
            <NavLink to="/blog" className={linkCls} onClick={() => setOpen(false)} data-testid="nav-mobile-blog">
              Blog
            </NavLink>
            {user ? (
              <>
                <NavLink to="/dashboard" className={linkCls} onClick={() => setOpen(false)} data-testid="nav-mobile-dashboard">
                  My registry
                </NavLink>
                <NavLink to="/thank-yous" className={linkCls} onClick={() => setOpen(false)} data-testid="nav-mobile-thank-yous">
                  Thank-yous
                </NavLink>
                <NavLink to="/settings" className={linkCls} onClick={() => setOpen(false)} data-testid="nav-mobile-settings">
                  Settings
                </NavLink>
                {user.role === "admin" && (
                  <NavLink to="/admin" className={linkCls} onClick={() => setOpen(false)} data-testid="nav-mobile-admin">
                    Admin
                  </NavLink>
                )}
                <button onClick={handleLogout} className="btn-ghost text-sm mt-2" data-testid="nav-mobile-logout">
                  Log out
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="btn-ghost text-sm mt-2" onClick={() => setOpen(false)} data-testid="nav-mobile-login">
                  Log in
                </Link>
                <Link to="/register" className="btn-primary text-sm" onClick={() => setOpen(false)} data-testid="nav-mobile-register">
                  Start yours
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
